// EN/FR translations for the Mise marketing website.
// Keep the tagline split so the italic phrase can be rendered in the orange serif font.

export const translations = {
  en: {
    nav: {
      home: "Home",
      atHome: "Households",
      familyMeals: "Food Service & Nonprofits",
      heartOfHouse: "Restaurant Operators",
      about: "About",
      media: "Media",
      contact: "Contact",
      pilotCta: "Pilot interest",
      pilotCtaAtHome: "Get the app",
      pilotCtaFamilyMeals: "Join the network",
      pilotCtaHoh: "Pilot interest",
    },
    common: {
      learnMore: "Learn more",
      joinBeta: "Join Google Play beta",
      joinNetwork: "Join the network",
      requestPilot: "Request a pilot",
      visitSite: "Visit misekitchen.ca",
      backHome: "Back to home",
    },
    footer: {
      legal: { label: "Legal", privacy: "Privacy Policy", terms: "Terms of Use" },
      brand: "Mise",
      domain: "misekitchen.ca",
      founder: "Sean Burton, Founder & CEO",
      taglineLead: "Food managed better. ",
      taglineItalic: "At home, in the community, and in the kitchen",
      // the footer shows the italic part on two lines
      taglineItalicA: "At home, in the community,",
      taglineItalicB: "and in the kitchen",
      builtIn: "Built in Calgary.",
      copyright: "© " + new Date().getFullYear() + " Mise. All rights reserved.",
      sections: {
        platforms: "Platforms",
        company: "Company",
        follow: "Follow Mise",
      },
      marketingLabel: "Marketing and press",
    },
    home: {
      heroEyebrow: "Calgary · Restaurant software for good",
      heroLineA: "The numbers don't lie. Neither do we.",
      heroLineBItalic: "We only win when you do.",
      heroMission:
        "Mise is a restaurant software company built to change the industry for the better: software that saves operators money, and a community network that keeps good food out of the bin.",
      heroCtaPrimary: "Our Story",
      heroCtaSecondary: "Talk to us",
      // The three hero cards, in this order: Heart of House, Family Meals, Mise at Home.
      pillars: {
        heartOfHouse: { verb: "Save operators money", text: "Know why your margin moved. Fix it. Prove it worked." },
        familyMeals: { verb: "Rescue surplus food", text: "Restaurants and grocers feed neighbours, in public, on the ground." },
        atHome: { verb: "Waste less at home", text: "Cook what you already own." },
      },
      connectEyebrow: "The Mise connection",
      connectHeading: "It all connects.",
      connectSub: "One app. One movement. Three platforms that only work because all three exist.",
      connectNodes: [
        { title: "Households", platform: "Mise at Home", text: "Less waste at home builds awareness in the neighbourhood." },
        { title: "Food rescue", platform: "Family Meals", text: "Restaurants feed neighbours and build trust in public." },
        { title: "Restaurants", platform: "Heart of House", text: "That trust becomes software that protects their margins." },
      ],
      believeEyebrow: "What we believe",
      beliefs: [
        { title: "We only win when you do.", text: "Heart of House can bill on proof: the greater of a small minimum or a share of the savings it can show, capped at 4% of food and beverage sales." },
        { title: "Nothing good goes in the bin.", text: "Surplus from restaurants and grocers goes to neighbourhood nonprofits through Family Meals. At home, Mise turns what's in the fridge into dinner." },
        { title: "Built by people who've worked the line.", text: "Every tool is designed by a chef who has run the kitchens it's built for, not by people guessing what operators need." },
      ],
      visionEyebrow: "Our vision",
      vision: "Independent restaurants stay open. Neighbours get fed. Nothing good goes to waste.",
      sidesEyebrow: "Two sides of one table",
      sidesHeading: "The money operators lose and the food that gets thrown out are the same problem.",
      sidesOpsEyebrow: "For operators",
      sidesOpsTitle: "Keep more of what you earn.",
      sidesCommunityEyebrow: "For the community",
      sidesCommunityTitle: "Put good food on tables, not in bins.",
      sidesAnswer: "Our answer",
      sidesOpsAfter: "saves money and proves it.",
      stats: [
        {
          value: "79 kg",
          label: "Food wasted at home per Canadian, every year.",
          source: "UN Environment Programme: Food Waste Index Report 2024",
        },
        {
          value: "25.5%",
          label: "Of Canadians live in food-insecure households.",
          source: "PROOF, University of Toronto: Canadian Income Survey, 2024",
        },
        {
          value: "41%",
          label: "Of Canadian restaurants are losing money or breaking even.",
          source: "Restaurants Canada, Q2 2026",
        },
      ],
      platformsEyebrow: "Platforms",
      platformsHeading: "Three platforms. One system.",
      infoChip: "Info",
      platforms: {
        heartOfHouse: {
          name: "Mise: Heart of House",
          tag: "SaaS · Founding pilots open",
          desc: "Save operators money. Know why your margin moved. Fix it. Prove it worked.",
        },
        familyMeals: {
          name: "Mise: Family Meals",
          tag: "Web · Free · Calgary",
          desc: "Rescue surplus food. Restaurants and grocers feed neighbours, in public, on the ground.",
        },
        atHome: {
          name: "Mise at Home",
          tag: "Mobile · Free",
          desc: "Waste less at home. Cook what you already own.",
        },
      },
      ctaBandHeading: "Ready to be first?",
      ctaBandText:
        "Founding pilots for Calgary restaurants. The waitlist for households. The network for nonprofits and food banks. One form, direct to us.",
      ctaBandButton: "Get in touch",
    },
    atHome: {
      metaTitle: "Mise at Home · Cook what you already own",
      eyebrow: "Platform 01 · Households",
      heading: "Mise at Home",
      subheading: "Cook what you already own.",
      subheadingItalic: "Waste less without thinking about it.",
      intro:
        "A free mobile app that turns the contents of your fridge into real meals, smarter grocery runs, and less food in the bin. Live on Google Play. In beta testing for the App Store.",
      playCta: "Get it on Google Play",
      playSmall: "Get it on",
      playBig: "Google Play",
      siteCta: "misehome.ca",
      appStoreNote: "iPhone: in beta testing for the App Store.",
      stat: {
        value: "79 kg",
        label: "Food wasted at home per Canadian, every year.",
        source: "UN Environment Programme: Food Waste Index Report 2024",
      },
      why: "Less waste at home is where the whole Mise system starts. Every meal cooked from what's already in the fridge is food that never hits the bin.",
      featuresEyebrow: "What's inside",
      featuresHeading: "Everything in the fridge. Nothing going to waste.",
      features: [
        {
          title: "Fridge scan",
          text: "Photograph what's already inside. Mise reads the shelves and builds a live inventory.",
        },
        {
          title: "Recipes from what you have",
          text: "Cook what's on hand first, with allergens flagged and approximate nutrition for every recipe.",
        },
        {
          title: "Smart grocery lists",
          text: "Built from your meal plan and what's already in the fridge, with storage tips so food lasts longer.",
        },
        {
          title: "Meal planner",
          text: "Plan the week around what expires first. Waste drops without thinking about it.",
        },
        {
          title: "Ask Chef AI",
          text: "A coach in your pocket. Technique, substitutions, knife work, sauce fixes.",
        },
        {
          title: "Nutrition & allergens",
          text: "Approximate per-plate macros and allergen flags for your household.",
        },
        {
          title: "50 achievements to earn",
          text: "From Fridge Master to Kitchen Alchemist: every recipe you generate, technique you learn, and meal you cook moves you forward.",
        },
        {
          title: "Share what you cook",
          text: "Every recipe has a native share button. Send the dish to a friend, post it, save it.",
        },
      ],
      nutritionNote:
        "Nutrition information in Mise at Home is AI-generated and approximate. It is not medical or dietary advice; check labels and talk to a professional about allergies or dietary needs.",
      galleryEyebrow: "A look inside",
      galleryHeading: "Five screens, one kitchen.",
      gallery: ["01 · Fridge inventory", "02 · AI recipe picks", "03 · Recipe & nutrition*", "04 · Technique walkthrough", "05 · Smart grocery list"],
      galleryNote: "* Nutrition shown is AI-generated and approximate.",
      showcaseEyebrow: "Smart grocery",
      showcaseHeading: "A list that helps food last.",
      showcaseBody:
        "Mise builds your list from the week's meal plan and what's already in the fridge, so you buy only what's missing.",
      showcasePoints: [
        { title: "Storage tips", text: "Every item comes with how to store it, so it lasts longer once it's home." },
        { title: "AI ingredient deep dive", text: "Tap any ingredient to learn more about it: what it is, how to use it and how to keep it." },
        { title: "Built from your plan", text: "The list comes from your meal plan and your fridge, not from guesswork." },
      ],
      readyHeading: "Get Mise at Home.",
      readySub: "Free on Google Play now. iPhone is in beta testing for the App Store.",
      waitlistHeading: "Not available in your country yet?",
      waitlistSub: "Join the waitlist and we'll email you when Mise at Home reaches you.",
      waitlist: {
        placeholder: "your@email.com",
        submit: "Join the waitlist",
        sending: "Joining...",
        success: "You're on the list. We'll email you when it reaches your country.",
        error: "Something went wrong. Try again in a minute.",
      },
    },
    familyMeals: {
      metaTitle: "Mise: Family Meals · Calgary food rescue network",
      eyebrow: "Platform 02 · Community",
      badge: "Now open in Calgary",
      heading: "Mise: Family Meals",
      subheading: "Surplus food, redirected with dignity.",
      intro:
        "Mise connects Calgary bakeries, restaurants and grocers with neighbourhood non-profits, so surplus food becomes tomorrow's meal, not waste.",
      joinCta: "Join the network",
      siteCta: "misefamilymeals.ca",
      free: "Free for food businesses and non-profits. Always.",
      impactLive: {
        eyebrow: "Live impact",
        kgLabel: "kg rescued",
        mealsLabel: "meals enabled",
        co2Label: "kg CO₂e avoided",
        note: "Live from misefamilymeals.ca. Counters only move when a drop-off is weighed; the first delivery will appear here.",
      },
      media: {
        eyebrow: "As seen in",
        quote: "If there's anything that we can do to help, why wouldn't we?",
        quoteBy: "Sean Burton, founder, to CTV News",
        read: "Read the story",
        all: "All press",
        items: [
          {
            outlet: "CTV News Calgary",
            date: "Oct 23, 2026",
            headline: "New online platform helps move end-of-day food surplus between businesses and charities",
            href: "https://www.ctvnews.ca/calgary/article/new-online-platform-helps-move-end-of-day-food-surplus-between-businesses-and-charities/",
          },
          {
            outlet: "Calgary Herald",
            date: "Oct 27, 2026",
            headline: "How to prevent food from going to waste: Calgary entrepreneurs connect businesses with non-profits",
            href: "https://calgaryherald.com/news/local-news/calgary-entrepreneurs-reducing-food-waste-supporting-non-profits",
          },
        ],
      },
      howEyebrow: "How it works",
      howHeading: "Three roles. One mission.",
      roles: [
        { who: "Restaurants & bakeries", title: "List what you can't use", text: "At close or any time during the day: add how it's been stored, a pickup window and allergen tags." },
        { who: "Non-profits", title: "Post your needs & receiving hours", text: "Tell the community what's most useful right now. Claim listings, or wait for partners to reach out." },
        { who: "Mise team", title: "Review, connect, celebrate", text: "We review listings for food safety, help partners find each other and share the impact." },
      ],
      screensEyebrow: "Inside the network",
      screensHeading: "The whole city, in one glance.",
      screens: ["Rescue Board", "Community Needs", "Map", "Impact"],
      safetyEyebrow: "Food safety, built in",
      safetyHeading: "Guardrails that protect every plate.",
      safety: [
        { title: "Acknowledged at listing", text: "Restaurants confirm food was safely stored before posting." },
        { title: "Allergen & temperature tags", text: "Visible at a glance so receivers can plan distribution safely." },
        { title: "Weighed drop-off record", text: "Each drop-off is confirmed in Mise with its weight, creating a traceable record." },
        { title: "Admin oversight", text: "Mise reviews listings and can pause any that raise a food-safety concern." },
      ],
      rescuedEyebrow: "What gets rescued",
      rescued: [
        { title: "Bread, pastries & baked goods", text: "Day-old, still good. Listed when it's ready, picked up in a set window." },
        { title: "Local produce", text: "Harvest surplus from growers and grocers, routed to neighbours instead of the green bin." },
        { title: "End-of-service meals", text: "Prepared food from the line, tagged for allergens and temperature." },
      ],
      joinEyebrow: "Three ways in. One network.",
      joinHeading: "You have surplus. We have neighbours who need it.",
      doors: [
        { title: "I have surplus to post", text: "Restaurants, bakeries, grocers and cafés. Free, a few minutes to set up.", cta: "Join as a food business" },
        { title: "I receive and distribute food", text: "Food banks, shelters, community fridges, meal programs and community groups.", cta: "Join as a non-profit" },
        { title: "I need food", text: "Find help nearby, quietly and without judgement, through the public Find Help directory.", cta: "Find Help" },
      ],
      note: "Mise doesn't buy, sell, store or transport food: food businesses and non-profits arrange pickups directly with each other.",
      joinForm: {
        eyebrow: "Family Meals · Join the network",
        heading: "Tell us where you fit.",
        sub: "Pick your role. The form updates to just the questions that matter for you.",
        roleLabel: "I am a…",
        roles: {
          supplier: "Restaurant / Bakery / Grocer",
          distributor: "Nonprofit / Food Bank",
        },
        common: {
          contactName: "Your name",
          email: "Email",
          phone: "Phone (optional)",
        },
        supplier: {
          businessName: "Business name",
          businessType: "Business type",
          businessTypePlaceholder: "Restaurant · Bakery · Grocer · Café · Other",
          location: "Location",
          locationPlaceholder: "Neighbourhood or full address",
          surplusVolume: "Monthly surplus volume (optional)",
          surplusVolumePlaceholder: "Rough weight in kg, or number of trays / cases",
          surplusNotes: "Tell us about your surplus",
          surplusNotesPlaceholder: "What comes off the line? Bread? Prepared meals? Produce? Bakery ends? How often, roughly?",
        },
        distributor: {
          organizationName: "Organization name",
          organizationType: "Type of organization",
          organizationTypePlaceholder: "Food bank · Shelter · Community fridge · Meal program · Other",
          serviceArea: "Service area in Calgary",
          serviceAreaPlaceholder: "Which neighbourhoods, wards, or communities do you serve?",
          capacity: "Current capacity / monthly meals served (optional)",
          capacityPlaceholder: "e.g. 800 meals / week, 4 pickups / week",
          communityNeeds: "What does your community need?",
          communityNeedsPlaceholder: "Protein? Fresh produce? Prepared meals? Dietary restrictions? Timing constraints?",
        },
        submit: "Join the network",
        sending: "Sending…",
        success: "Received. We'll be in touch about your next steps.",
        errorGeneric: "Something went wrong. Try again in a minute.",
        returnCta: "Back to Family Meals",
      },
      comingSoon: {
        heading: "Coming Soon",
        joinHeading: "Join the network",
        joinSub: "Whether you have surplus to give or capacity to move it — there's a place for you on the board.",
        joinCtaSupplier: "I have surplus to post",
        joinCtaDistributor: "I receive and distribute food",
      },
    },
    hoh: {
      eyebrow: "Platform 03 · Restaurants",
      launchBadge: "Founding pilots · now accepting restaurants",
      heading: "Mise: Heart of House",
      metaTitle: "Mise: Heart of House · Restaurant back-of-house software, from $199 a month",
      metaDescription: "Mise: Heart of House · Restaurant back-of-house software, from $199 a month",
      subheading: "Know why your margin moved. Fix it. Prove it worked.",
      intro:
        "Independent restaurants are running a margin war they cannot win with spreadsheets. Heart of House connects the systems you already use (POS, supplier invoices, inventory counts, recipes and labour), shows you where the money went, recommends the fixes, and measures what each one saved.",
      integrationsLabel: "Built to connect with",
      integrations: ["Square", "Toast", "Clover", "Lightspeed", "Genius", "Oracle MICROS", "7shifts", "Sysco", "Gordon Food Service", "OpenTable"],
      integrationsNote:
        "Connectors are built and being certified with each provider ahead of our founding pilots. No partnership with these companies is implied. Until a connection is certified, sales come in by file export and invoices by photo.",
      bookDemoCta: "Book a Demo",
      featuresEyebrow: "Inside the platform",
      featuresHeading: "What's running when you open the dashboard.",
      // Tabs on the page: `items` are positions in `features` below, `screen` a key of HOH_SCREENS.
      featureGroups: [
        { name: "Food cost", sub: "What every plate really costs.", items: [8, 1, 9, 4], screen: "recipe", caption: "Recipes · COGS computed live" },
        { name: "Menu", sub: "Which dishes earn their spot.", items: [0, 6], screen: "menuOverview", caption: "Menu Engineering · Portfolio composition" },
        { name: "Suppliers & inventory", sub: "What you buy, and what you pay for it.", items: [7, 2, 3, 10], screen: "menuRanked", caption: "Items ranked by Contribution Margin" },
        { name: "Labour & month-end", sub: "Shifts, overtime and the close.", items: [5, 11], screen: "sales", caption: "Sales · Hourly matrix and mix" },
      ],
      features: [
        {
          title: "Kasavana-Smith menu engineering",
          text: "Stars, plowhorses, puzzles and dogs, auto-classified from sales and margin.",
        },
        {
          title: "AI recipe scanning",
          text: "Drop a recipe in any format. Costed, scaled, and allergen-tagged in minutes.",
        },
        {
          title: "Supplier monopoly index",
          text: "See which suppliers own you and which categories actually have leverage.",
        },
        {
          title: "Automated negotiation emails",
          text: "Pre-drafted, data-backed supplier emails, sent on your letterhead, on your timeline.",
        },
        {
          title: "Waste logging",
          text: "Capture spoilage, prep waste, and comps in seconds. Stop guessing food cost.",
        },
        {
          title: "OT cliff alerts",
          text: "Catch overtime before it hits, by shift, by role, in real time.",
        },
        {
          title: "AI surplus specials",
          text: "Turn what's about to spoil into the special of the night, automatically.",
        },
        {
          title: "Inventory & supplier hub",
          text: "One source of truth for what's in the walk-in and what it cost to get there.",
        },
        {
          title: "Ingredient-level costing",
          text: "True plate costs down to the ingredient, updated automatically as supplier prices move. Included in every plan.",
        },
        {
          title: "Variance metrics",
          text: "Track which ingredients and dishes are bleeding money & get menu recommendations to reduce variance.",
        },
        {
          title: "Automatic prep lists",
          text: "Par lists write themselves based on your sales mix and get emailed directly to prep for the day shift.",
        },
        {
          title: "P&L closeout",
          text: "Month-end walkthrough with adjustments, operator notes, and a formatted closeout email to ownership.",
        },
      ],
      modelHeading: "Plans and pricing",
      modelLead: "Start at $199 a month. Move to pay-on-proof when your data is ready.",
      modelBody:
        "Core and Pro are simple monthly subscriptions, per location. Intelligence adds Mise's measurement engine: once a kitchen has about six months of its own data, the owner signs off on a starting point, and Mise bills the greater of $299 or its share of the savings it can prove, never both, and never more than 4% of that month's food and beverage sales.",
      modelFloorNote:
        "Add-ons: front of house (bar, pour cost, host stand) $150 a month · multi-location groups $250 a month flat, whatever the group's size. Annual Core and Pro plans get two months free. No setup fee; monthly plans are month to month. Prices in CAD, per location, before GST.",
      plans: [
        {
          name: "Core",
          tag: "Run the kitchen",
          price: "$199",
          per: "/ month per location",
          text: "Recipe and ingredient-level costing, AI invoice and recipe reading, inventory counts and order-to-par, waste log, allergen tracking, time clock and team tools.",
        },
        {
          name: "Pro",
          tag: "The smart kitchen",
          price: "$299",
          per: "/ month per location",
          text: "Everything in Core, plus menu engineering, prep lists and forecasts, actual-vs-ideal food cost, P&L and budgets, supplier price tracking and negotiation letters, labour cost and scheduling, and Ask Mise.",
        },
        {
          name: "Intelligence",
          tag: "Pay on proof",
          price: "$299",
          per: "or Mise's share of proven savings, whichever is greater",
          text: "Everything in Pro, plus ranked recommendations and measured savings with receipts. Never more than 4% of monthly food and beverage sales. Move back to Pro at the end of any month.",
        },
      ],
      pilotPlanFlag: "Founding pilots start here",
      billingHeading: "How Intelligence billing works",
      tiersHeading: "Intelligence: Mise's share, by strength of evidence",
      tiersCols: ["Tier", "When it applies", "Rate"],
      tiers: [
        { tier: "Direct: Proven", when: "Market-verified or holdout-confirmed savings", rate: "60%" },
        { tier: "Direct: Sequence", when: "Mise flagged it, you acted, costs dropped", rate: "45%" },
        { tier: "Environmental", when: "Your kitchen runs better overall with Mise", rate: "30%" },
      ],
      revCapNote:
        "The Intelligence bill is the greater of $299 or Mise's share, never both, and is capped at 4% of that month's food and beverage sales. Always.",
      exampleHeading: "What a restaurant pays (illustrative)",
      exampleRows: [
        { label: "Neighbourhood bistro on Core", value: "$199 / month" },
        { label: "Full-service with a bar: Pro + front of house", value: "$449 / month" },
        { label: "Group of 3 on Pro: $299 × 3 + one $250 group fee", value: "$1,147 / month" },
        { label: "$1M restaurant on Intelligence, mature year (financial model)", value: "about $757 / month" },
        { label: "Savings Mise proved that month", value: "$1,900 / month" },
        { label: "Kept by the restaurant after Mise's bill", value: "$1,143 / month", accent: true },
      ],
      chartAlt:
        "Waterfall chart: food cost rose $1,100, from $31,000 to $32,100, but the kitchen saved $1,900. Price effect +$2,400 and mix effect +$600 are not billed; the efficiency saving of $1,900 is billable.",
      chartCaption: "How Mise separates the savings it bills from cost changes it didn't cause.",
      exampleNote:
        "Illustrative only. Intelligence bills depend on each kitchen's certified starting point and the savings its own records can verify; we show the math before anything is billed.",
      protectionsHeading: "Your protections on Intelligence",
      protections: [
        "You sign off on the starting point before anything is billed. It's locked and can't be changed after the fact.",
        "Market price swings and changes in what guests order are never billed. Only savings your own records can verify.",
        "Disputes block invoices: if you don't approve it, we don't bill it.",
        "Every closed month is exportable, so your accountant can re-check every dollar.",
        "Capped at 4% of that month's food and beverage sales, and you can move back to Pro at the end of any month.",
      ],
      compareHeading: "The only restaurant platform that offers pay-on-proof.",
      compareCols: ["Mise: Heart of House", "Toast (incl. xtraCHEF)", "Restaurant365", "MarketMan"],
      compareRows: [
        { feature: "Pay-on-proof pricing option", values: ["yes", "no", "no", "no"] },
        { feature: "Menu engineering (Kasavana-Smith)", values: ["yes", "partial", "yes", "partial"] },
        { feature: "Supplier monopoly index", values: ["yes", "no", "no", "no"] },
        { feature: "AI recipe scanning", values: ["yes", "no", "no", "partial"] },
        { feature: "Surplus → special automation", values: ["yes", "no", "no", "no"] },
        { feature: "Ask Mise: questions answered from your own numbers", values: ["yes", "yes", "partial", "no"] },
      ],
      pilotHeading: "Become a founding pilot.",
      pilotSub:
        "A limited number of restaurants get Pro free for six months, then $199 a month for their first year, in exchange for feedback, permission to measure results and a published case study. No setup fee, no long contract. Get in touch to reserve a spot.",
      pilotForm: {
        eyebrow: "Founding pilots",
        heading: "Apply for a founding pilot place",
        sub: "We're opening founding pilot places for independent kitchens in Calgary. Founding pilots get Pro free for six months, then $199 a month for their first year, then the regular price. You work with us directly while we build what kitchens actually need. Tell us about your kitchen. Sean reviews every application himself.",
        labels: {
          name: "Your name",
          role: "Your role",
          email: "Work email",
          kitchen: "Kitchen name",
          city: "City",
          placeType: "Type of place",
          placeTypeOther: "What kind of place?",
          covers: "Covers per week",
          pos: "Current POS",
          posOther: "Which POS do you use?",
          costing: "What you use today for costing and inventory",
          costingApp: "Which one?",
          notes: "Anything else we should know?",
        },
        optional: "(optional)",
        pickOne: "Pick one",
        roles: { owner: "Owner", gm: "General manager", chef: "Chef", other: "Other" },
        placeTypes: { fine_dining: "Fine dining", casual: "Casual dining", fast_casual: "Fast casual", qsr: "Quick service", bar: "Bar", cafe: "Cafe", other: "Something else" },
        coversOptions: { under_300: "Under 300", "300_700": "300 to 700", "700_1500": "700 to 1,500", over_1500: "Over 1,500" },
        posOptions: { square: "Square", toast: "Toast", clover: "Clover", lightspeed: "Lightspeed", micros: "Oracle MICROS", genius: "Genius", touchbistro: "TouchBistro", no_pos: "No POS", other: "Other" },
        costingOptions: { spreadsheets: "Spreadsheets", another_app: "Another app", pen_paper: "Pen and paper", nothing_yet: "Nothing yet" },
        // The updates question is stored with the answer, word for word (same wording as the Heart of House app).
        updates: { question: "Can we send you occasional updates from Mise?", yes: "Yes, keep me posted", no: "No, just my application" },
        required: {
          name: "Tell us your name.",
          role: "Pick your role.",
          email: "Enter your work email.",
          kitchen: "Tell us your kitchen's name.",
          city: "Tell us your city.",
          placeType: "Pick the type of place.",
          covers: "Pick your covers per week.",
          pos: "Pick your POS.",
          costing: "Pick what you use today.",
          updates: "Choose Yes or No.",
        },
        submit: "Send my application",
        sending: "Sending...",
        success: "Thank you. Your application is in. Sean will look at it within 2 business days and email you either way. If it's a fit, the next step is a 30-minute call and a walkthrough of Heart of House in one of our demo kitchens.",
        errorGeneric: "We couldn't send it. Try again, or email support@misekitchen.ca.",
        returnCta: "Back to Heart of House",
      },
      // The founding pilot page, from "Founding Pilot Program for Restaurant Owners" (Oct 2026).
      pilotPage: {
        metaTitle: "Founding Pilots · Mise: Heart of House",
        eyebrow: "Founding pilot program",
        headingA: "Become one of",
        headingB: "fifteen founding kitchens.",
        intro: "Heart of House shows you where your food and labour money goes, what to fix first, and whether the fix worked. We're choosing a small group of restaurants to run it with us first, on founder terms.",
        applyCta: "Apply for a place",
        talkCta: "Talk to Sean",
        placesLabel: "Fifteen places, three founding groups",
        placesShort: "15 places",
        placesSub: "3 founding groups of 5",
        offerEyebrow: "The founding offer",
        offer: [
          { when: "Months 1 to 6", value: "Free", unit: "", text: "The full Pro plan, set up with you, with nothing to pay." },
          { when: "Next 12 months", value: "$199", unit: "/ month", text: "Founder price: Pro for the Core price. A $100 a month saving on the $299 list price." },
          { when: "After month 6", value: "No contract", unit: "", text: "The six free months are the commitment. After that, leave at the end of any month." },
        ],
        problem: "Most owners find out what happened to their margin when the statement arrives, weeks after the month is over. By then the waste is in the bin, the price increase is on three invoices and the overtime is paid.",
        groupsEyebrow: "Three founding groups",
        groupsHeading: "Five kitchens in each.",
        groups: [
          { name: "Calgary full-service", text: "Independents and small groups. Hands-on setup.", live: "Going live from January 2027" },
          { name: "Quick-service", text: "Franchisees and independents.", live: "Going live in spring 2027" },
          { name: "Seasonal kitchens", text: "Open part of the year. Set up online; first season free.", live: "Going live for May 2027" },
        ],
        timelineEyebrow: "How your six free months run",
        timelineHeading: "From first conversation to your choice.",
        timeline: [
          { when: "Week 0", title: "A conversation", text: "A call or a visit with Sean, a former chef, not a salesperson. If Heart of House doesn't fit your kitchen, we'll say so." },
          { when: "Week 1", title: "Set up with you", text: "We connect your point of sale, load your recipes and suppliers, and do your first count together. Invoices go in by photo." },
          { when: "Weeks 2 to 4", title: "First results", text: "Plate costs, your menu ranked by profit, and par levels from your own data." },
          { when: "Every week", title: "A check-in with Sean", text: "What changed, why it changed, and what to fix next. We document every check-in, and anything you tell us is broken goes to the front of the line." },
          { when: "About month 3", title: "Your starting point, signed by you", text: "After about 91 days of clean data, your kitchen gets its baseline. You review it and sign off on it, and it belongs to you." },
          { when: "Months 4 to 6", title: "See what it's worth before you pay a cent", text: "Each month you see what Heart of House measured in your kitchen, and what the Intelligence plan would have charged for it." },
          { when: "Month 6", title: "Your choice", text: "Stay on founder terms, choose Intelligence, or leave with your data. No pressure, no penalty." },
        ],
        giveHeading: "The commitment",
        give: [
          { title: "A case study", text: "We document your results on our end and publish them, under your name or anonymously. You see it before it goes out." },
          { title: "Regular feedback", text: "Good or bad. The bad is the most useful part." },
          { title: "Weekly check-ins", text: "A short chat with Sean each week. We take the notes." },
        ],
        getHeading: "Founding kitchens get",
        get: [
          { title: "Pro free for six months", text: "Then $199 a month for a year." },
          { title: "Setup done with you", text: "Not a login and a video." },
          { title: "Priority fixes", text: "For anything you name." },
          { title: "Your data, always", text: "Ask for a copy at any time." },
        ],
        applyBandHeading: "Ready to be one of the fifteen?",
        applyBandText: "The application takes about two minutes. Sean reads every one himself.",
        backToProgram: "Back to the founding pilot program",
        formEyebrow: "Apply for a founding place",
        formHeading: "Tell us about your kitchen.",
        formSub: "Sean reviews every application himself and replies within 2 business days, either way.",
        formSections: ["About you", "Your kitchen", "How you run it today"],
        seanEyebrow: "Talk to Sean",
        seanName: "Sean Burton, Founder and CEO",
        seanCreds: ["Former Red Seal chef", "20 years in kitchens", "Culinary Olympics double gold", "MBA"],
        seanText: "Sean onboards founding kitchens himself. Twenty minutes is enough to see whether Heart of House fits your kitchen.",
        seanEmail: "sean@misekitchen.ca",
        faqEyebrow: "Questions owners ask",
        faq: [
          { q: "How much of my team's time will this take?", a: "Setup is done with you in the first week. After that it's the routine most good kitchens already have: a weekly count and invoices photographed as they arrive, plus a short weekly check-in with us. If something takes longer than it should, that's a fix for us to make." },
          { q: "Do I have to change my point of sale?", a: "No. Heart of House sits beside your POS, not in place of it, and only reads from it. It never changes anything in your POS." },
          { q: "What happens to my data?", a: "It's yours. It's stored in Canada, never sold, and never shared with another restaurant. Ask for a copy of all of it, including your baseline, at any time." },
          { q: "What if it doesn't save me anything?", a: "Then you'll know before you pay a cent, because for six months you'll see exactly what was measured." },
          { q: "Is there a contract?", a: "The six free months are the commitment. After that, there's no contract: leave at the end of any month. There's no setup fee and no cancellation fee." },
          { q: "Is this finished software?", a: "It's built and running today on demonstration kitchens; the founding pilots are its first real kitchens. That's why founding terms are this good, and why your feedback shapes what we build next." },
          { q: "What's the commitment?", a: "Three things: the case study, regular feedback (good or bad) and a weekly check-in. We do the documenting on our end, including notes from every check-in, so your part is a few honest minutes a week. The case study can be anonymous, and you see it before it goes out." },
        ],
        smallPrint: "Heart of House is built and running on demonstration data; the first founding pilots go live in early 2027 and there are no paying customers yet. Prices are in Canadian dollars, per location, before GST; features and terms may change before launch.",
      },
      demo: {
        metaTitle: "Book a Demo · Mise: Heart of House",
        eyebrow: "Book a demo",
        heading: "Book a Demo",
        subheading: "Thirty minutes. A demo kitchen. A real human.",
        intro: "Tell us about your kitchen. We'll email you back within 2 business days, and a real human will propose a time that works.",
        steps: [
          { title: "We reply within 2 business days", text: "We read every request and come back with a few specific times." },
          { title: "A 30-minute walkthrough", text: "Heart of House in one of our demo kitchens, with real recipes, invoices and sales." },
          { title: "Your kitchen, your questions", text: "Bring your food cost, your POS and whatever keeps you up after close." },
        ],
        formHeading: "Tell us about your kitchen.",
        labels: {
          name: "Your name",
          email: "Work email",
          restaurant: "Restaurant / Group",
          role: "Your role",
          tier: "Select Your Tier",
          pos: "Current POS System",
          posOther: "Which POS do you use?",
          pain: "What's the biggest BOH pain right now?",
          availability: "When works for a 30-min demo?",
          referral: "How'd you hear about us?",
          referralOther: "Tell us where you heard about us",
        },
        placeholders: {
          pain: "e.g. food cost is drifting and we don't know where · variance is out of control · invoices are a binder nightmare",
          availability: "e.g. weekday mornings before service, Tue/Wed afternoons",
        },
        availabilityNote: "Free-form. We'll reply with a few specific options.",
        pickOne: "Pick one",
        roles: { owner: "Owner", gm: "GM / Operations", chef: "Chef / Exec Chef", sous_chef: "Sous Chef", bar_manager: "Bar Manager", director: "Director of F&B", other: "Other" },
        tiers: { core: "Core", pro: "Pro", intelligence: "Intelligence", unsure: "Unsure" },
        referralOptions: { google: "Google search", instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", restaurant_or_chef: "Another restaurant or chef", supplier: "A supplier", startup_tnt_or_event: "Startup TNT or an event", news: "News or an article", other: "Other" },
        required: { name: "Tell us your name.", email: "Enter your work email." },
        submit: "Send request",
        sending: "Sending...",
        promise: "We reply within 2 business days. Promise.",
        orEmail: "Or just email",
        successTitle: "Got it.",
        successBody: "Thanks. We'll reply to {email} within 2 business days.",
        another: "Send another request",
        errorGeneric: "We couldn't send it. Try again, or email support@misekitchen.ca.",
        interactiveBadge: "Coming in November",
        interactiveTitle: "Interactive Demo Coming in November",
        interactiveText: "Click through Heart of House on your own time, in a demo kitchen with real numbers. No call needed.",
        sneakPeek: "Sneak peek",
      },
    },
    about: {
      eyebrow: "About",
      heading: "Built to protect what makes hospitality worth loving: the",
      headingItalic: "Symphony of Service at its best.",
      subHeading:
        "Chefs should be orchestrating great service. Not buried behind clipboards trying to explain another missing five kilos of pulled pork.",
      intro:
        "Mise is the work of Sean Burton, built out of twenty years in professional kitchens, hard lessons from financial systems that rarely told the full truth, and a stubborn belief that the kitchen is where the math gets exposed.",
      pullQuote: "Damn right I made that.",
      photos: [
        { caption: "Surf & Earth", alt: "Surf & Earth, plated by Sean Burton" },
        { caption: "Petits Fours - IKA Culinary Olympics 2016", alt: "Petits fours at the IKA Culinary Olympics 2016, by Sean Burton" },
        { caption: "Plums", alt: "Plums, plated by Sean Burton" },
      ],
      storyHeading: "The founder",
      storyParas: [
        "For 20 years, I worked in restaurants trying to build order inside systems that rarely had any. I changed menus all at once when they needed to change, spent 80 to 100 hour weeks training cooks how to cook with precision and respect, and fought to protect the ingredients we worked with from becoming waste. Because to me, waste was never just a number. Every chicken breast tossed in the bin represented an animal we hadn't respected enough, a cost we failed to control, and a guest experience we were quietly compromising.",
        "One of the moments that never left me was walking into an ownership meeting with a quarterly financial statement in my hands after weeks of grinding to bring a kitchen into alignment. The team was clicking. They believed in what we were building. We knew where the costs were, we knew what needed fixing, and it felt like we were getting there. Then the statement hit, and it told a completely different story. Not just bad numbers. A loss of confidence. The daily systems we relied on were only showing half the picture.",
        "That was the real problem.",
        "Operators were being judged by the bottom line without having the tools to truly understand what was driving it. Owners didn't care where the bleeding came from, only that it existed. Training took time. Better systems took time. Strong food cost discipline took time. But the pressure was immediate, and too often the response was to make the food worse, smaller, cheaper, or easier to fake.",
        "At the same time, I knew what a great kitchen could feel like when it was working. Service at its best is a kind of symphony. The line moves in rhythm. Cooks watch out for each other without being asked. Plates from different stations land in the window at the same time for the same table. The chef's voice cuts through the noise with authority, and even in the chaos there is respect, precision, and pride. Guests feel it. Cooks feel it. You can see it when someone looks at a plate they just sent and thinks, damn right I made that.",
        "That is what I wanted to protect.",
        "I built Mise, and especially Heart of House, to give chefs and operators the tools they were always missing: systems that bring clarity, reduce waste, protect margins, and return focus to the craft. Chefs should be orchestrating great service, not buried behind clipboards and spreadsheets trying to explain another missing five kilos of pulled pork.",
        "The goal is simple:",
        "Remove the systemic burden that steals energy from creativity, so kitchens can get back to the work that makes hospitality worth loving.",
      ],
      signature: "Sean Burton, Founder & CEO, Mise",
      missionHeading: "Mission",
      missionText:
        "Mise exists to make food easier to manage, harder to waste, and more affordable for everyone: for households, communities, and the restaurants that feed them.",
      visionHeading: "Vision",
      visionText:
        "Connecting communities to reduce waste & food insecurity; and an industry leader in providing tech-driven efficiencies for restaurant operators' bottom line.",
      whyCalgaryHeading: "Why Calgary first",
      whyCalgaryText:
        "Mise was born in Calgary. This city's food culture runs deep: independent restaurants that punch above their weight, chefs who train the next generation, local growers and grocers, and a nonprofit network that shows up when asked. It's home, and it felt like the perfect place to start.",
    },
    media: {
      metaTitle: "Media · Mise",
      eyebrow: "Media",
      heading: "Mise in the",
      headingItalic: "news.",
      intro: "Press coverage of Mise and our own news, from Calgary kitchens to the community groups we work with.",
      filters: { all: "All", press: "In the press", post: "From Mise" },
      fromMise: "From Mise",
      featured: "Latest",
      read: "Read the story",
      watch: "Watch the story",
      listen: "Listen",
      readPost: "Read more",
      by: "By",
      empty: "Nothing here yet. Check back soon.",
      tags: {
        company: "Company",
        "family-meals": "Food Service & Nonprofits",
        "heart-of-house": "Restaurant Operators",
        "at-home": "Households",
      },
      pressKit: {
        eyebrow: "Press enquiries",
        heading: "Writing about Mise?",
        text: "We're happy to help with interviews, photos and background on Mise and our platforms. Email us and we'll get back to you quickly.",
        follow: "Follow Mise",
      },
      post: {
        back: "All media",
        share: "Copy link",
        copied: "Link copied",
        more: "More from Mise",
        notFound: "We couldn't find that post.",
      },
    },
    contact: {
      metaTitle: "Contact · Mise",
      eyebrow: "Contact",
      heading: "Let's talk.",
      headingItalic: "Pull up a chair at the pass.",
      sub:
        "One form, straight to us. Whether you run a kitchen, a food bank, or a community group, or you just want to know more.",
      replyNote: "We reply within 2 business days.",
      hqLabel: "Headquarters",
      hq: "Calgary, Alberta",
      form: {
        name: "Your name",
        email: "Email",
        restaurant: "Restaurant / organisation (optional)",
        city: "City",
        platform: "What's this about?",
        message: "How can we help?",
        submit: "Send message",
        sending: "Sending…",
        success: "Got it. Mise will be in touch.",
        errorGeneric: "Something went wrong. Try again in a minute.",
        platforms: {
          atHome: "Mise at Home",
          familyMeals: "Mise: Family Meals",
          familyMealsSupplier: "Family Meals: I have surplus",
          familyMealsDistributor: "Family Meals: I receive food",
          heartOfHouse: "Mise: Heart of House",
          partnership: "Partnership / press",
          other: "Other",
        },
        placeholders: {
          name: "First and last",
          email: "you@example.com",
          restaurant: "e.g. The 17th Ave room",
          city: "Calgary",
          message: "A sentence or two is enough.",
        },
      },
      directHeading: "Or write directly",
      supportLabel: "General questions & feedback",
      directEmail: "support@misekitchen.ca",
      marketingLabel: "Marketing, press & partnerships",
      followHeading: "Follow Mise",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      atHome: "Ménages",
      familyMeals: "Restauration et organismes",
      heartOfHouse: "Restaurateurs",
      about: "À propos",
      media: "Médias",
      contact: "Contact",
      pilotCta: "Projet pilote",
      pilotCtaAtHome: "Obtenir l'appli",
      pilotCtaFamilyMeals: "Rejoindre le réseau",
      pilotCtaHoh: "Projet pilote",
    },
    common: {
      learnMore: "En savoir plus",
      joinBeta: "Rejoindre la bêta Google Play",
      joinNetwork: "Rejoindre le réseau",
      requestPilot: "Demander un pilote",
      visitSite: "Visiter misekitchen.ca",
      backHome: "Retour à l'accueil",
    },
    footer: {
      legal: { label: "Mentions légales", privacy: "Politique de confidentialité", terms: "Conditions d'utilisation" },
      brand: "Mise",
      domain: "misekitchen.ca",
      founder: "Sean Burton, fondateur et chef de la direction",
      taglineLead: "Mieux gérer la nourriture. ",
      taglineItalic: "À la maison, dans la communauté et en cuisine",
      taglineItalicA: "À la maison, dans la communauté",
      taglineItalicB: "et en cuisine",
      builtIn: "Conçu à Calgary.",
      copyright: "© " + new Date().getFullYear() + " Mise. Tous droits réservés.",
      sections: {
        platforms: "Plateformes",
        company: "Entreprise",
        follow: "Suivre Mise",
      },
      marketingLabel: "Marketing et presse",
    },
    home: {
      heroEyebrow: "Calgary · Logiciel de restauration au service du bien commun",
      heroLineA: "Les chiffres ne mentent pas. Nous non plus.",
      heroLineBItalic: "On gagne seulement quand vous gagnez.",
      heroMission:
        "Mise est une entreprise de logiciels pour la restauration, bâtie pour changer l'industrie pour le mieux : un logiciel qui fait économiser les restaurateurs, et un réseau communautaire qui garde la bonne nourriture hors de la poubelle.",
      heroCtaPrimary: "Notre histoire",
      heroCtaSecondary: "Nous parler",
      pillars: {
        heartOfHouse: { verb: "Faire économiser les restaurateurs", text: "Comprenez pourquoi votre marge a bougé. Corrigez-la. Prouvez que ça a marché." },
        familyMeals: { verb: "Sauver les surplus", text: "Restaurants et épiciers nourrissent leurs voisins, au grand jour, sur le terrain." },
        atHome: { verb: "Gaspiller moins à la maison", text: "Cuisinez ce que vous avez déjà." },
      },
      connectEyebrow: "La connexion Mise",
      connectHeading: "Tout est lié.",
      connectSub: "Une appli. Un mouvement. Trois plateformes qui ne fonctionnent que parce que les trois existent.",
      connectNodes: [
        { title: "Ménages", platform: "Mise à la maison", text: "Moins de gaspillage à la maison sensibilise tout le quartier." },
        { title: "Sauvetage alimentaire", platform: "Repas en famille", text: "Les restaurants nourrissent leurs voisins et bâtissent la confiance au grand jour." },
        { title: "Restaurants", platform: "Cœur de cuisine", text: "Cette confiance devient un logiciel qui protège leurs marges." },
      ],
      believeEyebrow: "Ce que nous croyons",
      beliefs: [
        { title: "On gagne seulement quand vous gagnez.", text: "Cœur de cuisine peut facturer sur preuve : le plus élevé d'un petit minimum ou d'une part des économies qu'il peut démontrer, plafonné à 4 % des ventes d'aliments et de boissons." },
        { title: "Rien de bon ne va à la poubelle.", text: "Les surplus des restaurants et des épiciers vont aux organismes du quartier grâce à Repas en famille. À la maison, Mise transforme le contenu du frigo en souper." },
        { title: "Bâti par des gens qui ont travaillé en cuisine.", text: "Chaque outil est conçu par un chef qui a dirigé les cuisines pour lesquelles il est bâti, pas par des gens qui devinent ce dont les restaurateurs ont besoin." },
      ],
      visionEyebrow: "Notre vision",
      vision: "Les restaurants indépendants restent ouverts. Les voisins mangent à leur faim. Rien de bon n'est gaspillé.",
      sidesEyebrow: "Deux côtés d'une même table",
      sidesHeading: "L'argent que perdent les restaurateurs et la nourriture jetée sont un seul et même problème.",
      sidesOpsEyebrow: "Pour les restaurateurs",
      sidesOpsTitle: "Gardez plus de ce que vous gagnez.",
      sidesCommunityEyebrow: "Pour la communauté",
      sidesCommunityTitle: "La bonne nourriture sur les tables, pas dans les poubelles.",
      sidesAnswer: "Notre réponse",
      sidesOpsAfter: "fait économiser et le prouve.",
      stats: [
        {
          value: "79 kg",
          label: "Gaspillés à la maison par Canadien, chaque année.",
          source: "Programme des Nations Unies pour l'environnement : Food Waste Index 2024",
        },
        {
          value: "25,5 %",
          label: "Des Canadiens vivent en situation d'insécurité alimentaire.",
          source: "PROOF, Université de Toronto : Enquête canadienne sur le revenu, 2024",
        },
        {
          value: "41 %",
          label: "Des restaurants canadiens perdent de l'argent ou atteignent tout juste le seuil de rentabilité.",
          source: "Restaurants Canada, T2 2026",
        },
      ],
      platformsEyebrow: "Plateformes",
      platformsHeading: "Trois plateformes. Un seul système.",
      infoChip: "Info",
      platforms: {
        heartOfHouse: {
          name: "Mise : Cœur de cuisine",
          tag: "SaaS · Pilotes fondateurs ouverts",
          desc: "Faites économiser les restaurateurs. Comprenez pourquoi votre marge a bougé. Corrigez-la. Prouvez que ça a marché.",
        },
        familyMeals: {
          name: "Mise : Repas en famille",
          tag: "Web · Gratuit · Calgary",
          desc: "Sauvez les surplus. Restaurants et épiciers nourrissent leurs voisins, au grand jour, sur le terrain.",
        },
        atHome: {
          name: "Mise à la maison",
          tag: "Mobile · Gratuit",
          desc: "Gaspillez moins à la maison. Cuisinez ce que vous avez déjà.",
        },
      },
      ctaBandHeading: "Prêt à être le premier ?",
      ctaBandText:
        "Les pilotes fondateurs pour les restaurants de Calgary. La liste d'attente pour les ménages. Le réseau pour les organismes et les banques alimentaires. Un formulaire, directement à nous.",
      ctaBandButton: "Nous écrire",
    },
    atHome: {
      metaTitle: "Mise à la maison · Cuisinez ce que vous avez déjà",
      eyebrow: "Plateforme 01 · Ménages",
      heading: "Mise à la maison",
      subheading: "Cuisinez ce que vous avez déjà.",
      subheadingItalic: "Gaspillez moins sans même y penser.",
      intro:
        "Une appli mobile gratuite qui transforme le contenu de votre frigo en vrais repas, des courses plus malines et moins de nourriture à la poubelle. Offerte sur Google Play. En bêta pour l'App Store.",
      playCta: "Disponible sur Google Play",
      playSmall: "Disponible sur",
      playBig: "Google Play",
      siteCta: "misehome.ca",
      appStoreNote: "iPhone : en bêta pour l'App Store.",
      stat: {
        value: "79 kg",
        label: "Gaspillés à la maison par Canadien, chaque année.",
        source: "Programme des Nations Unies pour l'environnement : Food Waste Index 2024",
      },
      why: "Moins de gaspillage à la maison, c'est là que tout le système Mise commence. Chaque repas cuisiné avec ce qui est déjà au frigo, c'est de la nourriture qui ne finit jamais à la poubelle.",
      featuresEyebrow: "Au menu",
      featuresHeading: "Tout dans le frigo. Rien au gaspillage.",
      features: [
        {
          title: "Scan du frigo",
          text: "Photographiez ce qui s'y trouve. Mise lit les tablettes et bâtit un inventaire en direct.",
        },
        {
          title: "Recettes selon vos ingrédients",
          text: "Cuisinez d'abord ce qui est sur place, avec les allergènes signalés et une nutrition approximative pour chaque recette.",
        },
        {
          title: "Listes d'épicerie intelligentes",
          text: "Bâties à partir de votre plan de repas et de ce qui est déjà au frigo, avec des conseils de conservation pour que les aliments durent plus longtemps.",
        },
        {
          title: "Planificateur de repas",
          text: "Planifiez la semaine selon ce qui expire en premier. Le gaspillage diminue sans y penser.",
        },
        {
          title: "Demander à Chef IA",
          text: "Un coach dans votre poche. Technique, substitutions, découpe, correction de sauces.",
        },
        {
          title: "Nutrition et allergènes",
          text: "Macros approximatives par assiette et marqueurs d'allergènes pour votre foyer.",
        },
        {
          title: "50 défis à relever",
          text: "De Maître du Frigo à Alchimiste de la Cuisine : chaque recette générée, chaque technique apprise, chaque repas cuisiné vous fait avancer.",
        },
        {
          title: "Partagez vos plats",
          text: "Chaque recette a un bouton de partage natif. Envoyez le plat à un ami, publiez-le, sauvegardez-le.",
        },
      ],
      nutritionNote:
        "Les informations nutritionnelles de Mise à la maison sont générées par IA et approximatives. Elles ne constituent pas un avis médical ou diététique; vérifiez les étiquettes et consultez un professionnel pour les allergies ou les besoins alimentaires.",
      galleryEyebrow: "Aperçu",
      galleryHeading: "Cinq écrans, une cuisine.",
      gallery: ["01 · Inventaire du frigo", "02 · Recettes IA du jour", "03 · Recette et nutrition*", "04 · Pas-à-pas technique", "05 · Épicerie intelligente"],
      galleryNote: "* La nutrition affichée est générée par IA et approximative.",
      showcaseEyebrow: "Épicerie intelligente",
      showcaseHeading: "Une liste qui aide les aliments à durer.",
      showcaseBody:
        "Mise bâtit votre liste à partir du plan de repas de la semaine et de ce qui est déjà au frigo, pour que vous n'achetiez que ce qui manque.",
      showcasePoints: [
        { title: "Conseils de conservation", text: "Chaque article vient avec la façon de le conserver, pour qu'il dure plus longtemps une fois à la maison." },
        { title: "Exploration IA des ingrédients", text: "Touchez n'importe quel ingrédient pour en savoir plus : ce que c'est, comment l'utiliser et comment le garder." },
        { title: "Bâtie selon votre plan", text: "La liste vient de votre plan de repas et de votre frigo, pas de suppositions." },
      ],
      readyHeading: "Obtenez Mise à la maison.",
      readySub: "Gratuite sur Google Play dès maintenant. La version iPhone est en bêta pour l'App Store.",
      waitlistHeading: "Pas encore offerte dans votre pays?",
      waitlistSub: "Inscrivez-vous à la liste d'attente et nous vous écrirons quand Mise à la maison sera offerte chez vous.",
      waitlist: {
        placeholder: "vous@exemple.com",
        submit: "Rejoindre la liste",
        sending: "Inscription...",
        success: "Vous êtes sur la liste. Nous vous écrirons quand l'appli arrivera dans votre pays.",
        error: "Une erreur est survenue. Réessayez dans une minute.",
      },
    },
    familyMeals: {
      metaTitle: "Mise : Repas en famille · Réseau de sauvetage alimentaire de Calgary",
      eyebrow: "Plateforme 02 · Communauté",
      badge: "Maintenant ouvert à Calgary",
      heading: "Mise : Repas en famille",
      subheading: "Des surplus redirigés avec dignité.",
      intro:
        "Mise relie les boulangeries, restaurants et épiciers de Calgary aux organismes de quartier, pour que les surplus deviennent le repas de demain plutôt que du gaspillage.",
      joinCta: "Rejoindre le réseau",
      siteCta: "misefamilymeals.ca",
      free: "Gratuit pour les commerces alimentaires et les organismes. Toujours.",
      impactLive: {
        eyebrow: "Impact en direct",
        kgLabel: "kg sauvés",
        mealsLabel: "repas rendus possibles",
        co2Label: "kg CO₂e évités",
        note: "En direct de misefamilymeals.ca. Les compteurs ne bougent que lorsqu'un dépôt est pesé; la première livraison apparaîtra ici.",
      },
      media: {
        eyebrow: "Vu dans les médias",
        quote: "If there's anything that we can do to help, why wouldn't we?",
        quoteBy: "Sean Burton, fondateur, à CTV News",
        read: "Lire l'article",
        all: "Toute la presse",
        items: [
          {
            outlet: "CTV News Calgary",
            date: "23 oct. 2026",
            headline: "New online platform helps move end-of-day food surplus between businesses and charities",
            href: "https://www.ctvnews.ca/calgary/article/new-online-platform-helps-move-end-of-day-food-surplus-between-businesses-and-charities/",
          },
          {
            outlet: "Calgary Herald",
            date: "27 oct. 2026",
            headline: "How to prevent food from going to waste: Calgary entrepreneurs connect businesses with non-profits",
            href: "https://calgaryherald.com/news/local-news/calgary-entrepreneurs-reducing-food-waste-supporting-non-profits",
          },
        ],
      },
      howEyebrow: "Comment ça marche",
      howHeading: "Trois rôles. Une mission.",
      roles: [
        { who: "Restaurants et boulangeries", title: "Affichez ce que vous ne pouvez pas utiliser", text: "À la fermeture ou à tout moment de la journée : indiquez comment c'était conservé, une plage de cueillette et les allergènes." },
        { who: "Organismes", title: "Affichez vos besoins et heures de réception", text: "Dites à la communauté ce qui est le plus utile en ce moment. Réclamez des annonces, ou attendez que des partenaires vous contactent." },
        { who: "Équipe Mise", title: "Vérifier, relier, célébrer", text: "Nous vérifions les annonces pour la salubrité, aidons les partenaires à se trouver et partageons l'impact." },
      ],
      screensEyebrow: "Au cœur du réseau",
      screensHeading: "Toute la ville, d'un seul coup d'œil.",
      screens: ["Tableau des sauvetages", "Besoins de la communauté", "Carte", "Impact"],
      safetyEyebrow: "Salubrité intégrée",
      safetyHeading: "Des garde-fous qui protègent chaque assiette.",
      safety: [
        { title: "Confirmation à l'affichage", text: "Les restaurants confirment que la nourriture a été conservée de façon sécuritaire avant de l'afficher." },
        { title: "Allergènes et température", text: "Visibles en un coup d'œil pour que les receveurs planifient la distribution en toute sécurité." },
        { title: "Dépôt pesé et consigné", text: "Chaque dépôt est confirmé dans Mise avec son poids, ce qui crée un registre traçable." },
        { title: "Supervision", text: "Mise vérifie les annonces et peut suspendre celles qui soulèvent une préoccupation de salubrité." },
      ],
      rescuedEyebrow: "Ce qui est sauvé",
      rescued: [
        { title: "Pain, viennoiseries et produits de boulangerie", text: "D'hier, encore bons. Affichés quand ils sont prêts, ramassés dans une plage définie." },
        { title: "Produits locaux", text: "Les surplus des producteurs et épiciers, dirigés vers les voisins plutôt que vers le bac vert." },
        { title: "Repas de fin de service", text: "Des plats préparés, avec allergènes et température indiqués." },
      ],
      joinEyebrow: "Trois façons d'entrer. Un seul réseau.",
      joinHeading: "Vous avez des surplus. Nous avons des voisins qui en ont besoin.",
      doors: [
        { title: "J'ai des surplus à afficher", text: "Restaurants, boulangeries, épiciers et cafés. Gratuit, quelques minutes pour s'inscrire.", cta: "S'inscrire comme commerce" },
        { title: "Je reçois et distribue de la nourriture", text: "Banques alimentaires, refuges, frigos communautaires, programmes de repas et groupes communautaires.", cta: "S'inscrire comme organisme" },
        { title: "J'ai besoin de nourriture", text: "Trouvez de l'aide près de chez vous, discrètement et sans jugement, grâce au répertoire public Trouver de l'aide.", cta: "Trouver de l'aide" },
      ],
      note: "Mise n'achète, ne vend, n'entrepose et ne transporte pas de nourriture : les commerces et les organismes organisent les cueillettes directement entre eux.",
      joinForm: {
        eyebrow: "Repas en famille · Rejoindre le réseau",
        heading: "Dites-nous où vous vous situez.",
        sub: "Choisissez votre rôle. Le formulaire s'ajuste aux questions qui vous concernent.",
        roleLabel: "Je suis…",
        roles: {
          supplier: "Restaurant / Boulangerie / Épicier",
          distributor: "Organisme / Banque alimentaire",
        },
        common: {
          contactName: "Votre nom",
          email: "Courriel",
          phone: "Téléphone (facultatif)",
        },
        supplier: {
          businessName: "Nom du commerce",
          businessType: "Type de commerce",
          businessTypePlaceholder: "Restaurant · Boulangerie · Épicier · Café · Autre",
          location: "Emplacement",
          locationPlaceholder: "Quartier ou adresse complète",
          surplusVolume: "Volume mensuel de surplus (facultatif)",
          surplusVolumePlaceholder: "Poids approx. en kg, ou nombre de plateaux / caisses",
          surplusNotes: "Parlez-nous de votre surplus",
          surplusNotesPlaceholder: "Qu'est-ce qui sort de la ligne ? Pain ? Plats préparés ? Fruits et légumes ? Fins de boulangerie ? À quelle fréquence, en gros ?",
        },
        distributor: {
          organizationName: "Nom de l'organisme",
          organizationType: "Type d'organisme",
          organizationTypePlaceholder: "Banque alimentaire · Refuge · Frigo communautaire · Programme de repas · Autre",
          serviceArea: "Territoire desservi à Calgary",
          serviceAreaPlaceholder: "Quels quartiers, arrondissements ou communautés desservez-vous ?",
          capacity: "Capacité actuelle / repas servis par mois (facultatif)",
          capacityPlaceholder: "p. ex. 800 repas / semaine, 4 collectes / semaine",
          communityNeeds: "De quoi votre communauté a-t-elle besoin ?",
          communityNeedsPlaceholder: "Protéines ? Fruits et légumes frais ? Plats préparés ? Restrictions alimentaires ? Contraintes de temps ?",
        },
        submit: "Rejoindre le réseau",
        sending: "Envoi…",
        success: "Reçu. Nous reviendrons vers vous sur les prochaines étapes.",
        errorGeneric: "Une erreur est survenue. Réessayez dans une minute.",
        returnCta: "Retour à Repas en famille",
      },
      comingSoon: {
        heading: "Bientôt disponible",
        joinHeading: "Rejoindre le réseau",
        joinSub: "Que vous ayez du surplus à offrir ou la capacité de le redistribuer — il y a une place pour vous.",
        joinCtaSupplier: "J'ai du surplus à publier",
        joinCtaDistributor: "Je reçois et distribue des aliments",
      },
    },
    hoh: {
      eyebrow: "Plateforme 03 · Restaurants",
      launchBadge: "Pilotes fondateurs · inscriptions ouvertes",
      heading: "Mise : Cœur de cuisine",
      metaTitle: "Mise : Cœur de cuisine · Logiciel d'arrière-cuisine pour restaurants, à partir de 199 $ par mois",
      metaDescription: "Mise : Cœur de cuisine · Logiciel d'arrière-cuisine pour restaurants, à partir de 199 $ par mois",
      subheading: "Comprenez pourquoi votre marge a bougé. Corrigez-la. Prouvez que ça a marché.",
      intro:
        "Les restaurants indépendants mènent une guerre des marges qu'aucun tableur ne peut gagner. Cœur de cuisine relie les systèmes que vous utilisez déjà (PDV, factures fournisseurs, inventaires, recettes et main-d'œuvre), vous montre où est passé l'argent, recommande les correctifs et mesure ce que chacun a permis d'économiser.",
      integrationsLabel: "Conçu pour se connecter à",
      integrations: ["Square", "Toast", "Clover", "Lightspeed", "Genius", "Oracle MICROS", "7shifts", "Sysco", "Gordon Food Service", "OpenTable"],
      integrationsNote:
        "Les connecteurs sont conçus et en cours de certification avec chaque fournisseur en vue de nos pilotes fondateurs. Aucun partenariat avec ces entreprises n'est sous-entendu. D'ici à ce qu'une connexion soit certifiée, les ventes arrivent par exportation de fichier et les factures, par photo.",
      bookDemoCta: "Réserver une démo",
      featuresEyebrow: "Au cœur de la plateforme",
      featuresHeading: "Ce qui tourne quand vous ouvrez le tableau de bord.",
      featureGroups: [
        { name: "Coût des aliments", sub: "Ce que chaque assiette coûte vraiment.", items: [8, 1, 9, 4], screen: "recipe", caption: "Recettes · COGS calculés en direct" },
        { name: "Menu", sub: "Les plats qui méritent leur place.", items: [0, 6], screen: "menuOverview", caption: "Ingénierie de menu · Composition du portfolio" },
        { name: "Fournisseurs et inventaire", sub: "Ce que vous achetez, et ce que vous le payez.", items: [7, 2, 3, 10], screen: "menuRanked", caption: "Items classés par marge de contribution" },
        { name: "Main-d'œuvre et fin de mois", sub: "Les quarts, les heures supp et la clôture.", items: [5, 11], screen: "sales", caption: "Ventes · Matrice horaire et mix-produit" },
      ],
      features: [
        {
          title: "Ingénierie de menu Kasavana-Smith",
          text: "Vedettes, chevaux de trait, casse-têtes et boulets, classifiés automatiquement.",
        },
        {
          title: "Lecture IA des recettes",
          text: "Déposez une recette, peu importe le format. Coûtée, mise à l'échelle, allergènes repérés.",
        },
        {
          title: "Indice de monopole fournisseurs",
          text: "Voyez quels fournisseurs vous contrôlent et où vous avez vraiment du levier.",
        },
        {
          title: "Courriels de négociation automatisés",
          text: "Pré-rédigés et appuyés par les données, envoyés à votre rythme.",
        },
        {
          title: "Journal des pertes",
          text: "Capturez les pertes, le prep, les compliments. Cessez de deviner le coût des aliments.",
        },
        {
          title: "Alertes seuil de temps supp.",
          text: "Détectez les heures supp avant qu'elles ne frappent, par quart, par rôle.",
        },
        {
          title: "Spéciaux surplus IA",
          text: "Transformez ce qui va se gâter en spécial du soir, automatiquement.",
        },
        {
          title: "Inventaire et fournisseurs",
          text: "Une seule source de vérité pour ce qui est en chambre froide et ce qu'il a coûté.",
        },
        {
          title: "Coût de revient à l'ingrédient près",
          text: "Le vrai coût de chaque assiette, ingrédient par ingrédient, mis à jour automatiquement quand les prix des fournisseurs bougent. Inclus dans tous les forfaits.",
        },
        {
          title: "Métriques de variance",
          text: "Suivez quels ingrédients et plats perdent de l'argent et obtenez des recommandations de menu pour réduire la variance.",
        },
        {
          title: "Listes de préparation automatiques",
          text: "Les listes de par se rédigent d'elles-mêmes selon votre mix de ventes et sont envoyées par courriel à la préparation pour le quart de jour.",
        },
        {
          title: "Clôture de P&L",
          text: "Revue de fin de mois avec ajustements, notes d'opérateur, et courriel de clôture formaté pour la direction.",
        },
      ],
      modelHeading: "Forfaits et tarifs",
      modelLead: "À partir de 199 $ par mois. Passez au paiement sur preuve quand vos données sont prêtes.",
      modelBody:
        "Core et Pro sont de simples abonnements mensuels, par établissement. Intelligence ajoute le moteur de mesure de Mise : une fois qu'une cuisine a environ six mois de ses propres données, le propriétaire approuve un point de départ, et Mise facture le plus élevé de 299 $ ou de sa part des économies qu'il peut prouver, jamais les deux, et jamais plus de 4 % des ventes d'aliments et de boissons du mois.",
      modelFloorNote:
        "Modules complémentaires : salle (bar, coût par verre, accueil) 150 $ par mois · groupes multi-établissements 250 $ par mois, forfaitaire, peu importe la taille du groupe. Les forfaits annuels Core et Pro incluent deux mois gratuits. Aucuns frais d'installation; les forfaits mensuels sont sans engagement, de mois en mois. Prix en dollars canadiens, par établissement, avant TPS.",
      plans: [
        {
          name: "Core",
          tag: "Faire rouler la cuisine",
          price: "199 $",
          per: "/ mois par établissement",
          text: "Coût de revient des recettes à l'ingrédient près, lecture IA des factures et des recettes, inventaires et commandes au niveau de par, journal des pertes, suivi des allergènes, horodateur et outils d'équipe.",
        },
        {
          name: "Pro",
          tag: "La cuisine intelligente",
          price: "299 $",
          per: "/ mois par établissement",
          text: "Tout ce qu'offre Core, plus l'ingénierie de menu, les listes de préparation et les prévisions, le coût des aliments réel vs théorique, l'état des résultats et les budgets, le suivi des prix fournisseurs et les lettres de négociation, le coût et la planification de la main-d'œuvre, et Demandez à Mise.",
        },
        {
          name: "Intelligence",
          tag: "Paiement sur preuve",
          price: "299 $",
          per: "ou la part de Mise des économies prouvées, selon le plus élevé",
          text: "Tout ce qu'offre Pro, plus des recommandations classées par priorité et des économies mesurées, pièces justificatives à l'appui. Jamais plus de 4 % des ventes mensuelles d'aliments et de boissons. Revenez à Pro à la fin de n'importe quel mois.",
        },
      ],
      pilotPlanFlag: "Les pilotes fondateurs commencent ici",
      billingHeading: "Comment fonctionne la facturation Intelligence",
      tiersHeading: "Intelligence : la part de Mise, selon la solidité des preuves",
      tiersCols: ["Niveau", "Quand", "Taux"],
      tiers: [
        { tier: "Direct : Prouvé", when: "Économies vérifiées par le marché ou confirmées par groupe témoin", rate: "60 %" },
        { tier: "Direct : Séquence", when: "Mise l'a signalé, vous avez agi, les coûts ont baissé", rate: "45 %" },
        { tier: "Environnemental", when: "Votre cuisine fonctionne globalement mieux avec Mise", rate: "30 %" },
      ],
      revCapNote:
        "La facture Intelligence correspond au plus élevé de 299 $ ou de la part de Mise, jamais les deux, et est plafonnée à 4 % des ventes d'aliments et de boissons du mois. Toujours.",
      exampleHeading: "Ce que paie un restaurant (à titre indicatif)",
      exampleRows: [
        { label: "Bistro de quartier sur Core", value: "199 $ / mois" },
        { label: "Service complet avec bar : Pro + salle", value: "449 $ / mois" },
        { label: "Groupe de 3 sur Pro : 299 $ × 3 + un forfait de groupe de 250 $", value: "1 147 $ / mois" },
        { label: "Restaurant de 1 M$ sur Intelligence, année de croisière (modèle financier)", value: "environ 757 $ / mois" },
        { label: "Économies prouvées par Mise ce mois-là", value: "1 900 $ / mois" },
        { label: "Ce que garde le restaurant après la facture de Mise", value: "1 143 $ / mois", accent: true },
      ],
      chartAlt:
        "Graphique en cascade : le coût des aliments a augmenté de 1 100 $, de 31 000 $ à 32 100 $, mais la cuisine a économisé 1 900 $. L'effet prix (+2 400 $) et l'effet de composition (+600 $) ne sont pas facturés; l'économie d'efficacité de 1 900 $ est facturable.",
      chartCaption: "Comment Mise sépare les économies qu'il facture des variations de coûts qu'il n'a pas causées.",
      exampleNote:
        "À titre indicatif seulement. Les factures Intelligence dépendent du point de départ certifié de chaque cuisine et des économies que ses propres registres peuvent vérifier; nous montrons les calculs avant toute facturation.",
      protectionsHeading: "Vos protections avec Intelligence",
      protections: [
        "Vous approuvez le point de départ avant toute facturation. Il est verrouillé et ne peut pas être modifié après coup.",
        "Les fluctuations des prix du marché et les changements dans ce que commandent les clients ne sont jamais facturés. Seules les économies que vos propres registres peuvent vérifier le sont.",
        "Les contestations bloquent les factures : si vous n'approuvez pas, on ne facture pas.",
        "Chaque mois clôturé est exportable, pour que votre comptable puisse revérifier chaque dollar.",
        "Plafonné à 4 % des ventes d'aliments et de boissons du mois, et vous pouvez revenir à Pro à la fin de n'importe quel mois.",
      ],
      compareHeading: "La seule plateforme de restauration qui offre le paiement sur preuve.",
      compareCols: ["Mise : Cœur de cuisine", "Toast (incl. xtraCHEF)", "Restaurant365", "MarketMan"],
      compareRows: [
        { feature: "Option de paiement sur preuve", values: ["oui", "non", "non", "non"] },
        { feature: "Ingénierie de menu (Kasavana-Smith)", values: ["oui", "partiel", "oui", "partiel"] },
        { feature: "Indice de monopole fournisseurs", values: ["oui", "non", "non", "non"] },
        { feature: "Lecture IA des recettes", values: ["oui", "non", "non", "partiel"] },
        { feature: "Surplus → spécial automatisé", values: ["oui", "non", "non", "non"] },
        { feature: "Demandez à Mise : des réponses tirées de vos propres chiffres", values: ["oui", "oui", "partiel", "non"] },
      ],
      pilotHeading: "Devenez pilote fondateur.",
      pilotSub:
        "Un nombre limité de restaurants obtiennent Pro gratuitement pendant six mois, puis à 199 $ par mois pour leur première année, en échange de leurs commentaires, de la permission de mesurer les résultats et d'une étude de cas publiée. Aucuns frais d'installation, aucun long contrat. Contactez-nous pour réserver votre place.",
      pilotForm: {
        eyebrow: "Pilotes fondateurs",
        heading: "Postulez pour une place de pilote fondateur",
        sub: "Nous ouvrons des places de pilote fondateur pour les cuisines indépendantes de Calgary. Les pilotes fondateurs obtiennent Pro gratuitement pendant six mois, puis à 199 $ par mois pour leur première année, puis le prix régulier. Vous travaillez directement avec nous pendant que nous construisons ce dont les cuisines ont vraiment besoin. Parlez-nous de votre cuisine. Sean examine lui-même chaque candidature.",
        labels: {
          name: "Votre nom",
          role: "Votre rôle",
          email: "Courriel professionnel",
          kitchen: "Nom de la cuisine",
          city: "Ville",
          placeType: "Type d'établissement",
          placeTypeOther: "Quel type d'établissement?",
          covers: "Couverts par semaine",
          pos: "Point de vente actuel",
          posOther: "Quel point de vente utilisez-vous?",
          costing: "Ce que vous utilisez aujourd'hui pour le coût de revient et l'inventaire",
          costingApp: "Laquelle?",
          notes: "Autre chose que nous devrions savoir?",
        },
        optional: "(facultatif)",
        pickOne: "Choisissez",
        roles: { owner: "Propriétaire", gm: "Directeur général", chef: "Chef", other: "Autre" },
        placeTypes: { fine_dining: "Grande cuisine", casual: "Restaurant décontracté", fast_casual: "Restauration rapide décontractée", qsr: "Service rapide", bar: "Bar", cafe: "Café", other: "Autre chose" },
        coversOptions: { under_300: "Moins de 300", "300_700": "300 à 700", "700_1500": "700 à 1 500", over_1500: "Plus de 1 500" },
        posOptions: { square: "Square", toast: "Toast", clover: "Clover", lightspeed: "Lightspeed", micros: "Oracle MICROS", genius: "Genius", touchbistro: "TouchBistro", no_pos: "Aucun PDV", other: "Autre" },
        costingOptions: { spreadsheets: "Tableurs", another_app: "Une autre application", pen_paper: "Papier et crayon", nothing_yet: "Rien pour l'instant" },
        updates: { question: "Pouvons-nous vous envoyer des nouvelles de Mise de temps en temps?", yes: "Oui, tenez-moi au courant", no: "Non, seulement ma candidature" },
        required: {
          name: "Indiquez votre nom.",
          role: "Choisissez votre rôle.",
          email: "Entrez votre courriel professionnel.",
          kitchen: "Indiquez le nom de votre cuisine.",
          city: "Indiquez votre ville.",
          placeType: "Choisissez le type d'établissement.",
          covers: "Choisissez vos couverts par semaine.",
          pos: "Choisissez votre point de vente.",
          costing: "Choisissez ce que vous utilisez aujourd'hui.",
          updates: "Choisissez Oui ou Non.",
        },
        submit: "Envoyer ma candidature",
        sending: "Envoi...",
        success: "Merci. Votre candidature est bien reçue. Sean l'examinera d'ici 2 jours ouvrables et vous écrira dans tous les cas. Si cela convient, la prochaine étape est un appel de 30 minutes et une visite de Heart of House dans l'une de nos cuisines de démonstration.",
        errorGeneric: "Nous n'avons pas pu l'envoyer. Réessayez ou écrivez à support@misekitchen.ca.",
        returnCta: "Retour à Cœur de cuisine",
      },
      pilotPage: {
        metaTitle: "Pilotes fondateurs · Mise : Cœur de cuisine",
        eyebrow: "Programme des pilotes fondateurs",
        headingA: "Devenez l'une des",
        headingB: "quinze cuisines fondatrices.",
        intro: "Cœur de cuisine vous montre où va l'argent de vos aliments et de votre main-d'œuvre, quoi corriger en premier, et si la correction a fonctionné. Nous choisissons un petit groupe de restaurants pour l'utiliser avec nous en premier, aux conditions fondatrices.",
        applyCta: "Postuler pour une place",
        talkCta: "Parler à Sean",
        placesLabel: "Quinze places, trois groupes fondateurs",
        placesShort: "15 places",
        placesSub: "3 groupes fondateurs de 5",
        offerEyebrow: "L'offre fondatrice",
        offer: [
          { when: "Mois 1 à 6", value: "Gratuit", unit: "", text: "Le forfait Pro complet, configuré avec vous, sans rien à payer." },
          { when: "Les 12 mois suivants", value: "199 $", unit: "/ mois", text: "Prix fondateur : Pro au prix de Core. Une économie de 100 $ par mois sur le prix courant de 299 $." },
          { when: "Après le mois 6", value: "Sans contrat", unit: "", text: "Les six mois gratuits sont l'engagement. Ensuite, partez à la fin de n'importe quel mois." },
        ],
        problem: "La plupart des propriétaires découvrent ce qui est arrivé à leur marge quand l'état financier arrive, des semaines après la fin du mois. Les pertes sont déjà à la poubelle, la hausse de prix est sur trois factures et les heures supp sont payées.",
        groupsEyebrow: "Trois groupes fondateurs",
        groupsHeading: "Cinq cuisines dans chacun.",
        groups: [
          { name: "Service complet à Calgary", text: "Indépendants et petits groupes. Configuration en personne.", live: "En service dès janvier 2027" },
          { name: "Service rapide", text: "Franchisés et indépendants.", live: "En service au printemps 2027" },
          { name: "Cuisines saisonnières", text: "Ouvertes une partie de l'année. Configuration en ligne; première saison gratuite.", live: "En service pour mai 2027" },
        ],
        timelineEyebrow: "Le déroulement de vos six mois gratuits",
        timelineHeading: "De la première conversation à votre choix.",
        timeline: [
          { when: "Semaine 0", title: "Une conversation", text: "Un appel ou une visite avec Sean, un ancien chef, pas un vendeur. Si Cœur de cuisine ne convient pas à votre cuisine, on vous le dira." },
          { when: "Semaine 1", title: "Configuré avec vous", text: "Nous connectons votre point de vente, chargeons vos recettes et vos fournisseurs, et faisons votre premier inventaire ensemble. Les factures entrent par photo." },
          { when: "Semaines 2 à 4", title: "Premiers résultats", text: "Le coût de chaque assiette, votre menu classé par profit et des niveaux de par tirés de vos propres données." },
          { when: "Chaque semaine", title: "Un suivi avec Sean", text: "Ce qui a changé, pourquoi, et quoi corriger ensuite. Nous documentons chaque suivi, et tout ce que vous signalez comme brisé passe en tête de file." },
          { when: "Vers le mois 3", title: "Votre point de départ, signé par vous", text: "Après environ 91 jours de données propres, votre cuisine obtient son point de référence. Vous l'examinez et l'approuvez, et il vous appartient." },
          { when: "Mois 4 à 6", title: "Voyez ce que ça vaut avant de payer un sou", text: "Chaque mois, vous voyez ce que Cœur de cuisine a mesuré dans votre cuisine, et ce que le forfait Intelligence aurait facturé." },
          { when: "Mois 6", title: "Votre choix", text: "Restez aux conditions fondatrices, choisissez Intelligence, ou partez avec vos données. Aucune pression, aucune pénalité." },
        ],
        giveHeading: "L'engagement",
        give: [
          { title: "Une étude de cas", text: "Nous documentons vos résultats de notre côté et les publions, sous votre nom ou de façon anonyme. Vous la voyez avant sa publication." },
          { title: "Des commentaires réguliers", text: "Bons ou mauvais. Les mauvais sont les plus utiles." },
          { title: "Des suivis hebdomadaires", text: "Une courte discussion avec Sean chaque semaine. Nous prenons les notes." },
        ],
        getHeading: "Les cuisines fondatrices reçoivent",
        get: [
          { title: "Pro gratuit pendant six mois", text: "Puis 199 $ par mois pendant un an." },
          { title: "Une configuration faite avec vous", text: "Pas un identifiant et une vidéo." },
          { title: "Des correctifs prioritaires", text: "Pour tout ce que vous signalez." },
          { title: "Vos données, toujours", text: "Demandez-en une copie en tout temps." },
        ],
        applyBandHeading: "Prêt à être l'une des quinze?",
        applyBandText: "La candidature prend environ deux minutes. Sean les lit toutes lui-même.",
        backToProgram: "Retour au programme des pilotes fondateurs",
        formEyebrow: "Postuler pour une place fondatrice",
        formHeading: "Parlez-nous de votre cuisine.",
        formSub: "Sean examine lui-même chaque candidature et répond d'ici 2 jours ouvrables, dans tous les cas.",
        formSections: ["À propos de vous", "Votre cuisine", "Votre façon de faire aujourd'hui"],
        seanEyebrow: "Parler à Sean",
        seanName: "Sean Burton, fondateur et PDG",
        seanCreds: ["Ancien chef Sceau rouge", "20 ans en cuisine", "Double médaille d'or aux Olympiques culinaires", "MBA"],
        seanText: "Sean accompagne lui-même les cuisines fondatrices. Vingt minutes suffisent pour voir si Cœur de cuisine convient à votre cuisine.",
        seanEmail: "sean@misekitchen.ca",
        faqEyebrow: "Les questions des propriétaires",
        faq: [
          { q: "Combien de temps cela prendra-t-il à mon équipe?", a: "La configuration se fait avec vous la première semaine. Ensuite, c'est la routine que la plupart des bonnes cuisines ont déjà : un inventaire hebdomadaire et les factures photographiées à leur arrivée, plus un court suivi hebdomadaire avec nous. Si quelque chose prend plus de temps que prévu, c'est à nous de le corriger." },
          { q: "Dois-je changer mon point de vente?", a: "Non. Cœur de cuisine fonctionne à côté de votre point de vente, pas à sa place, et ne fait qu'y lire. Il ne change jamais rien dans votre point de vente." },
          { q: "Qu'arrive-t-il à mes données?", a: "Elles vous appartiennent. Elles sont stockées au Canada, jamais vendues et jamais partagées avec un autre restaurant. Demandez-en une copie complète, y compris votre point de référence, en tout temps." },
          { q: "Et si ça ne me fait rien économiser?", a: "Vous le saurez avant de payer un sou, parce que pendant six mois vous verrez exactement ce qui a été mesuré." },
          { q: "Y a-t-il un contrat?", a: "Les six mois gratuits sont l'engagement. Ensuite, aucun contrat : partez à la fin de n'importe quel mois. Aucuns frais d'installation ni frais d'annulation." },
          { q: "Est-ce un logiciel terminé?", a: "Il est construit et fonctionne aujourd'hui dans des cuisines de démonstration; les pilotes fondateurs sont ses premières vraies cuisines. C'est pourquoi les conditions fondatrices sont aussi bonnes, et pourquoi vos commentaires orientent ce que nous construisons ensuite." },
          { q: "Quel est l'engagement?", a: "Trois choses : l'étude de cas, des commentaires réguliers (bons ou mauvais) et un suivi hebdomadaire. Nous faisons la documentation de notre côté, y compris les notes de chaque suivi; votre part, ce sont quelques minutes d'honnêteté par semaine. L'étude de cas peut être anonyme, et vous la voyez avant sa publication." },
        ],
        smallPrint: "Cœur de cuisine est construit et fonctionne avec des données de démonstration; les premiers pilotes fondateurs seront en service au début de 2027 et il n'y a pas encore de clients payants. Prix en dollars canadiens, par établissement, avant TPS; les fonctionnalités et les conditions peuvent changer avant le lancement.",
      },
      demo: {
        metaTitle: "Réserver une démo · Mise : Cœur de cuisine",
        eyebrow: "Réserver une démo",
        heading: "Réserver une démo",
        subheading: "Trente minutes. Une cuisine de démonstration. Une vraie personne.",
        intro: "Parlez-nous de votre cuisine. On vous répond par courriel d'ici 2 jours ouvrables, et une vraie personne vous proposera un moment qui vous convient.",
        steps: [
          { title: "Réponse d'ici 2 jours ouvrables", text: "On lit chaque demande et on revient avec quelques moments précis." },
          { title: "Une visite guidée de 30 minutes", text: "Cœur de cuisine dans l'une de nos cuisines de démonstration, avec de vraies recettes, factures et ventes." },
          { title: "Votre cuisine, vos questions", text: "Apportez votre coût des aliments, votre point de vente et ce qui vous empêche de dormir après la fermeture." },
        ],
        formHeading: "Parlez-nous de votre cuisine.",
        labels: {
          name: "Votre nom",
          email: "Courriel professionnel",
          restaurant: "Restaurant / groupe",
          role: "Votre poste",
          tier: "Choisissez votre forfait",
          pos: "Système de point de vente actuel",
          posOther: "Quel point de vente utilisez-vous?",
          pain: "Quel est votre plus gros casse-tête en cuisine en ce moment?",
          availability: "Quand une démo de 30 min vous convient-elle?",
          referral: "Comment avez-vous entendu parler de nous?",
          referralOther: "Dites-nous où vous avez entendu parler de nous",
        },
        placeholders: {
          pain: "p. ex. le coût des aliments dérive et on ne sait pas où · les écarts sont hors de contrôle · les factures sont un cauchemar de cartables",
          availability: "p. ex. les matins de semaine avant le service, mardi/mercredi après-midi",
        },
        availabilityNote: "Texte libre. On vous répondra avec quelques options précises.",
        pickOne: "Choisissez",
        roles: { owner: "Propriétaire", gm: "Directeur général / opérations", chef: "Chef / chef exécutif", sous_chef: "Sous-chef", bar_manager: "Gérant de bar", director: "Directeur de la restauration", other: "Autre" },
        tiers: { core: "Core", pro: "Pro", intelligence: "Intelligence", unsure: "Je ne sais pas encore" },
        referralOptions: { google: "Recherche Google", instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", restaurant_or_chef: "Un autre restaurant ou chef", supplier: "Un fournisseur", startup_tnt_or_event: "Startup TNT ou un événement", news: "Les nouvelles ou un article", other: "Autre" },
        required: { name: "Indiquez votre nom.", email: "Entrez votre courriel professionnel." },
        submit: "Envoyer la demande",
        sending: "Envoi...",
        promise: "On répond d'ici 2 jours ouvrables. Promis.",
        orEmail: "Ou écrivez simplement à",
        successTitle: "C'est noté.",
        successBody: "Merci. On vous répondra à {email} d'ici 2 jours ouvrables.",
        another: "Envoyer une autre demande",
        errorGeneric: "Nous n'avons pas pu l'envoyer. Réessayez ou écrivez à support@misekitchen.ca.",
        interactiveBadge: "En novembre",
        interactiveTitle: "Démo interactive offerte en novembre",
        interactiveText: "Explorez Cœur de cuisine à votre rythme, dans une cuisine de démonstration avec de vrais chiffres. Aucun appel requis.",
        sneakPeek: "Aperçu",
      },
    },
    about: {
      eyebrow: "À propos",
      heading: "Bâti pour protéger ce qui rend l'hospitalité digne d'être aimée : la",
      headingItalic: "Symphonie du Service à son meilleur.",
      subHeading:
        "Les chefs devraient orchestrer un grand service. Pas enterrés derrière des planchettes à essayer d'expliquer encore cinq kilos de porc effiloché manquants.",
      intro:
        "Mise est le travail de Sean Burton, bâti à partir de vingt ans en cuisine professionnelle, de leçons dures tirées de systèmes financiers qui racontaient rarement toute la vérité, et de la conviction tenace que la cuisine est l'endroit où les chiffres sont mis à nu.",
      pullQuote: "Ouais, c'est moi qui l'ai fait.",
      photos: [
        { caption: "Surf & Earth", alt: "Surf & Earth, dressé par Sean Burton" },
        { caption: "Petits fours - Olympiades culinaires IKA 2016", alt: "Petits fours aux Olympiades culinaires IKA 2016, par Sean Burton" },
        { caption: "Prunes", alt: "Prunes, dressé par Sean Burton" },
      ],
      storyHeading: "Le fondateur",
      storyParas: [
        "Pendant 20 ans, j'ai travaillé en restauration à essayer de bâtir de l'ordre dans des systèmes qui en avaient rarement. J'ai changé des menus d'un seul coup quand ils devaient changer, passé des semaines de 80 à 100 heures à former des cuisiniers à cuisiner avec précision et respect, et je me suis battu pour protéger les ingrédients avec lesquels on travaillait afin qu'ils ne deviennent pas des pertes. Parce que pour moi, la perte n'a jamais été juste un chiffre. Chaque poitrine de poulet jetée à la poubelle représentait un animal qu'on n'avait pas assez respecté, un coût qu'on n'avait pas su contrôler, et une expérience client qu'on compromettait discrètement.",
        "Un des moments qui ne m'a jamais quitté, c'est celui où je suis entré dans une réunion avec la direction, un état financier trimestriel dans les mains, après des semaines à trimer pour aligner une cuisine. L'équipe cliquait. Ils croyaient à ce qu'on bâtissait. On savait où étaient les coûts, on savait ce qu'il fallait régler, et on avait l'impression d'y arriver. Puis l'état financier est tombé, et il racontait une tout autre histoire. Pas juste de mauvais chiffres. Une perte de confiance. Les systèmes quotidiens sur lesquels on comptait ne montraient que la moitié du portrait.",
        "C'était ça, le vrai problème.",
        "Les exploitants étaient jugés sur la ligne du bas sans avoir les outils pour vraiment comprendre ce qui la faisait bouger. Les propriétaires ne se souciaient pas d'où venait l'hémorragie, seulement du fait qu'elle existait. La formation prenait du temps. De meilleurs systèmes prenaient du temps. Une discipline solide sur le coût des aliments prenait du temps. Mais la pression était immédiate, et trop souvent la réponse était de rendre la nourriture pire, plus petite, moins chère, ou plus facile à truquer.",
        "En même temps, je savais ce que ça pouvait donner, une grande cuisine quand elle fonctionne. Le service à son meilleur, c'est une sorte de symphonie. La ligne bouge en rythme. Les cuisiniers veillent les uns sur les autres sans qu'on leur demande. Les assiettes de différentes stations atterrissent au passe en même temps pour la même table. La voix du chef perce le bruit avec autorité, et même dans le chaos il y a du respect, de la précision et de la fierté. Les clients le sentent. Les cuisiniers le sentent. On le voit quand quelqu'un regarde une assiette qu'il vient de passer et se dit : ouais, c'est moi qui l'ai fait.",
        "C'est ça que je voulais protéger.",
        "J'ai bâti Mise, et surtout Cœur de cuisine, pour donner aux chefs et aux exploitants les outils qu'ils n'ont jamais eus : des systèmes qui apportent de la clarté, réduisent le gaspillage, protègent les marges, et ramènent l'attention sur le métier. Les chefs devraient orchestrer un grand service, pas être enterrés derrière des planchettes et des tableurs à essayer d'expliquer encore cinq kilos de porc effiloché manquants.",
        "L'objectif est simple :",
        "Retirer le fardeau systémique qui vole l'énergie à la créativité, pour que les cuisines puissent retourner au travail qui rend l'hospitalité digne d'être aimée.",
      ],
      signature: "Sean Burton, fondateur et chef de la direction, Mise",
      missionHeading: "Mission",
      missionText:
        "Mise existe pour rendre la nourriture plus facile à gérer, plus difficile à gaspiller et plus abordable pour tous : pour les ménages, les communautés et les restaurants qui les nourrissent.",
      visionHeading: "Vision",
      visionText:
        "Relier les communautés pour réduire le gaspillage et l'insécurité alimentaire, et être un chef de file des gains d'efficacité technologiques qui protègent la rentabilité des restaurateurs.",
      whyCalgaryHeading: "Pourquoi Calgary d'abord",
      whyCalgaryText:
        "Mise est né à Calgary. La culture culinaire de cette ville est riche : des restaurants indépendants qui frappent au-dessus de leur poids, des chefs qui forment la relève, des producteurs et épiciers locaux, et un réseau d'organismes qui répond présent. C'est chez nous, et c'était l'endroit idéal pour commencer.",
    },
    media: {
      metaTitle: "Médias · Mise",
      eyebrow: "Médias",
      heading: "Mise dans les",
      headingItalic: "nouvelles.",
      intro: "La couverture médiatique de Mise et nos propres nouvelles, des cuisines de Calgary aux organismes communautaires avec qui nous travaillons.",
      filters: { all: "Tout", press: "Dans la presse", post: "De Mise" },
      fromMise: "De Mise",
      featured: "À la une",
      read: "Lire l'article",
      watch: "Voir le reportage",
      listen: "Écouter",
      readPost: "Lire la suite",
      by: "Par",
      empty: "Rien pour l'instant. Revenez bientôt.",
      tags: {
        company: "Entreprise",
        "family-meals": "Restauration et organismes",
        "heart-of-house": "Restaurateurs",
        "at-home": "Ménages",
      },
      pressKit: {
        eyebrow: "Demandes des médias",
        heading: "Vous écrivez sur Mise?",
        text: "Nous pouvons vous aider avec des entrevues, des photos et de l'information sur Mise et nos plateformes. Écrivez-nous et nous vous répondrons rapidement.",
        follow: "Suivre Mise",
      },
      post: {
        back: "Tous les médias",
        share: "Copier le lien",
        copied: "Lien copié",
        more: "Plus de Mise",
        notFound: "Nous n'avons pas trouvé cette publication.",
      },
    },
    contact: {
      metaTitle: "Contact · Mise",
      eyebrow: "Contact",
      heading: "Parlons-nous.",
      headingItalic: "Prenez place au passe.",
      sub:
        "Un formulaire, directement à nous. Que vous teniez une cuisine, une banque alimentaire ou un groupe communautaire, ou que vous vouliez simplement en savoir plus.",
      replyNote: "Nous répondons d'ici 2 jours ouvrables.",
      hqLabel: "Siège social",
      hq: "Calgary, Alberta",
      form: {
        name: "Votre nom",
        email: "Courriel",
        restaurant: "Restaurant / organisme (facultatif)",
        city: "Ville",
        platform: "De quoi s'agit-il ?",
        message: "Comment peut-on aider ?",
        submit: "Envoyer le message",
        sending: "Envoi…",
        success: "Reçu. Mise reviendra vers vous.",
        errorGeneric: "Une erreur est survenue. Réessayez dans une minute.",
        platforms: {
          atHome: "Mise à la maison",
          familyMeals: "Mise : Repas en famille",
          familyMealsSupplier: "Repas en famille : j'ai des surplus",
          familyMealsDistributor: "Repas en famille : je reçois des aliments",
          heartOfHouse: "Mise : Cœur de cuisine",
          partnership: "Partenariat / presse",
          other: "Autre",
        },
        placeholders: {
          name: "Prénom et nom",
          email: "vous@exemple.com",
          restaurant: "p. ex. La salle sur la 17e",
          city: "Calgary",
          message: "Une ou deux phrases suffisent.",
        },
      },
      directHeading: "Ou écrivez directement",
      supportLabel: "Questions générales et commentaires",
      directEmail: "support@misekitchen.ca",
      marketingLabel: "Marketing, presse et partenariats",
      followHeading: "Suivre Mise",
    },
  },
};
